/**
 * Round-media playback rules, platform-neutral.
 *
 * Caddie's native player (`apps/caddie/src/components/RoundMediaScreen.tsx`)
 * and the GTG GC web viewer are two hosts for ONE set of playback rules, and
 * this module is those rules as pure TypeScript: what a member's tap means
 * given what the player has said so far, and what the host must then do to
 * its player. `bin/mobile contract caddie` copies it verbatim, behind a
 * generated-file banner, into
 * `apps/caddie/src/lib/round-media-playback.generated.ts`, so the app never
 * imports the engine and both hosts can run identical logic (Caddie's player
 * adopts its copy in a follow-up; today only the web viewer executes it). THIS copy is the
 * source of truth and `__tests__/roundMediaPlayback.test.ts` its canonical
 * suite; every rule below was transcribed from the native player, not
 * reinvented.
 *
 * A host feeds platform events in (expo-video events natively, `<video>`
 * events on the web) and executes the returned effects against its player.
 * Because the file lands in an isolated React Native project it imports
 * nothing, produces no member-facing copy (the `m:ss` time is its only
 * string; labels are the hosts' job) and names nothing that reads as a hook.
 */

export const VIDEO_CONTROL_IDLE_MS = 3_000
export const VIDEO_CONTROL_FADE_MS = 180
export const VIDEO_TIME_UPDATE_SECONDS = 0.25
export const VIDEO_END_TOLERANCE_SECONDS = VIDEO_TIME_UPDATE_SECONDS + 0.01
export const VIDEO_SEEK_STEP_SECONDS = 5

export type PlaybackStatus = 'idle' | 'loading' | 'readyToPlay' | 'error'
export type SurfaceAction = 'play' | 'pause' | 'replay'
export type SoundAction = 'unavailable' | 'unmute' | 'mute'

export interface PlaybackState {
  readonly status: PlaybackStatus
  readonly isPlaying: boolean
  /** The member's intent; starts false and survives a re-buffer. */
  readonly playbackRequested: boolean
  /** The native `playIssuedRef`, as state: a play the host has been told to issue. */
  readonly playIssued: boolean
  readonly ended: boolean
  readonly firstFrameRendered: boolean
  /** Starts true: a clip opens silent until the member asks for sound. */
  readonly muted: boolean
  /** `media.audio_present === true`; anything else leaves the sound control inert. */
  readonly audioPresent: boolean
  /** The last accepted sample, bounded to the effective duration. */
  readonly position: number
  /** What the player itself reports; 0 until known. */
  readonly sourceDuration: number
  /** `media.duration_seconds ?? 0` — may read 20.1 while the clip is 20.0. */
  readonly wireDuration: number
  /** Native holds a play until `readyToPlay`; the web issues it at once. */
  readonly deferPlayUntilReady: boolean
  readonly controlsFaded: boolean
  readonly screenReaderEnabled: boolean
  /** Bumps on every wake; hosts key their idle timer on it. */
  readonly wakeCount: number
}

export type PlaybackEvent =
  | { type: 'statusChange'; status: PlaybackStatus; duration?: number }
  | { type: 'playingChange'; isPlaying: boolean; status?: PlaybackStatus }
  | { type: 'mutedChange'; muted: boolean }
  | { type: 'timeUpdate'; currentTime: number; duration?: number }
  | { type: 'durationChange'; duration: number }
  | { type: 'playToEnd' }
  | { type: 'firstFrameRender' }
  | { type: 'screenReaderChange'; enabled: boolean }
  | { type: 'wake' }
  | { type: 'controlsFaded' }
  | { type: 'togglePlayback' }
  | { type: 'toggleSound' }
  | { type: 'seek'; position: number }
  | { type: 'stepSeek'; direction: 1 | -1 }

export type PlaybackEffect =
  | { type: 'play' }
  | { type: 'pause' }
  | { type: 'replay' }
  | { type: 'seek'; position: number }
  | { type: 'setMuted'; muted: boolean }
  | { type: 'fail' }

export interface PlaybackTransition {
  readonly state: PlaybackState
  readonly effects: readonly PlaybackEffect[]
}

export function initialPlaybackState(init: {
  wireDurationSeconds: number | null
  audioPresent: boolean | null
  deferPlayUntilReady: boolean
}): PlaybackState {
  return {
    status: 'idle',
    isPlaying: false,
    playbackRequested: false,
    playIssued: false,
    ended: false,
    firstFrameRendered: false,
    muted: true,
    audioPresent: init.audioPresent === true,
    position: 0,
    sourceDuration: 0,
    wireDuration: finiteVideoDuration(init.wireDurationSeconds ?? 0),
    deferPlayUntilReady: init.deferPlayUntilReady,
    controlsFaded: false,
    screenReaderEnabled: false,
    wakeCount: 0,
  }
}

/**
 * Apply one event, then settle the deferred play: the native effect keyed on
 * `[ended, playbackRequested, status]` issues `play` once the player is ready
 * (or at once, for a host that does not defer), and never twice.
 */
export function reducePlayback(state: PlaybackState, event: PlaybackEvent): PlaybackTransition {
  const applied = applyEvent(state, event)
  const next = applied.state
  const playAllowed = next.status === 'readyToPlay' || !next.deferPlayUntilReady
  if (!playAllowed || !next.playbackRequested || next.playIssued || next.ended) return applied
  return {
    state: { ...next, playIssued: true },
    effects: [...applied.effects, { type: 'play' }],
  }
}

function applyEvent(state: PlaybackState, event: PlaybackEvent): PlaybackTransition {
  switch (event.type) {
    case 'statusChange': {
      const next = { ...withReportedDuration(state, event.duration), status: event.status }
      const entersError = event.status === 'error' && state.status !== 'error'
      return { state: next, effects: entersError ? [{ type: 'fail' }] : [] }
    }
    case 'playingChange': {
      const status = event.status ?? state.status
      const base = { ...state, isPlaying: event.isPlaying, status }
      if (event.isPlaying) {
        return quiet(wake({ ...base, playIssued: true, playbackRequested: true, ended: false }))
      }
      // A pause reported while ready is the intent being dropped; a pause
      // while buffering keeps it, so the surface still reads "pause".
      if (status === 'readyToPlay') {
        return quiet(wake({ ...base, playIssued: false, playbackRequested: false }))
      }
      return quiet(wake(base))
    }
    case 'mutedChange':
      return quiet(wake({ ...state, muted: event.muted }))
    case 'timeUpdate': {
      if (!Number.isFinite(event.currentTime)) return quiet(state)
      const learned = withReportedDuration(state, event.duration)
      const duration = effectiveDuration(learned)
      const bounded = clampVideoTime(event.currentTime, duration)
      // The trailing samples after playToEnd sit inside one update interval
      // of the end; they keep the timeline pinned rather than un-ending it.
      if (learned.ended && duration - bounded <= VIDEO_END_TOLERANCE_SECONDS) return quiet(learned)
      return quiet({ ...learned, ended: false, position: bounded })
    }
    case 'durationChange':
      return quiet({ ...state, sourceDuration: finiteVideoDuration(event.duration) })
    case 'playToEnd':
      return quiet(
        wake({
          ...state,
          ended: true,
          playIssued: false,
          playbackRequested: false,
          isPlaying: false,
          position: effectiveDuration(state),
        }),
      )
    case 'firstFrameRender':
      return quiet(wake({ ...state, firstFrameRendered: true }))
    case 'screenReaderChange': {
      const next = { ...state, screenReaderEnabled: event.enabled }
      return quiet(event.enabled ? wake(next) : next)
    }
    case 'wake':
      return quiet(wake(state))
    case 'controlsFaded':
      return quiet(controlsMayFade(state) ? { ...state, controlsFaded: true } : state)
    case 'togglePlayback': {
      const woken = wake(state)
      if (state.ended) {
        // The timeline keeps reading the duration until a sample moves it.
        return {
          state: { ...woken, ended: false, playbackRequested: true, playIssued: true },
          effects: [{ type: 'replay' }],
        }
      }
      if (state.isPlaying || state.playbackRequested) {
        return {
          state: { ...woken, playIssued: false, playbackRequested: false },
          effects: [{ type: 'pause' }],
        }
      }
      return quiet({ ...woken, playbackRequested: true })
    }
    case 'toggleSound': {
      if (!state.audioPresent) return quiet(state)
      const muted = !state.muted
      return { state: wake({ ...state, muted }), effects: [{ type: 'setMuted', muted }] }
    }
    case 'seek':
      return seekTransition(state, event.position)
    case 'stepSeek':
      return seekTransition(state, stepSeek(state, event.direction))
  }
}

function quiet(state: PlaybackState): PlaybackTransition {
  return { state, effects: [] }
}

function wake(state: PlaybackState): PlaybackState {
  return { ...state, controlsFaded: false, wakeCount: state.wakeCount + 1 }
}

/** Take a duration the player reports alongside an event; ignore an unknown one. */
function withReportedDuration(state: PlaybackState, duration: number | undefined): PlaybackState {
  if (duration === undefined) return state
  const known = finiteVideoDuration(duration)
  if (known <= 0 || known === state.sourceDuration) return state
  return { ...state, sourceDuration: known }
}

function seekTransition(state: PlaybackState, position: number): PlaybackTransition {
  const duration = effectiveDuration(state)
  const bounded = clampVideoTime(position, duration)
  const woken = wake({ ...state, position: bounded })
  const atEnd = duration > 0 && bounded >= duration
  const next = atEnd
    ? { ...woken, ended: true, playIssued: false, playbackRequested: false, isPlaying: false }
    : { ...woken, ended: false }
  return { state: next, effects: [{ type: 'seek', position: bounded }] }
}

export function finiteVideoDuration(duration: number): number {
  return Number.isFinite(duration) ? Math.max(0, duration) : 0
}

export function clampVideoTime(position: number, duration: number): number {
  if (!Number.isFinite(position)) return 0
  return Math.max(0, Math.min(duration, position))
}

/** The player's own duration once it has one; the wire's until then. */
export function effectiveDuration(state: PlaybackState): number {
  return state.sourceDuration > 0 ? state.sourceDuration : state.wireDuration
}

/** Where the timeline reads: pinned to the end once the clip has ended. */
export function shownPosition(state: PlaybackState): number {
  const duration = effectiveDuration(state)
  return state.ended ? duration : clampVideoTime(state.position, duration)
}

export function progressFraction(state: PlaybackState): number {
  const duration = effectiveDuration(state)
  return duration > 0 ? shownPosition(state) / duration : 0
}

export function surfaceAction(state: PlaybackState): SurfaceAction {
  if (state.ended) return 'replay'
  return state.isPlaying || state.playbackRequested ? 'pause' : 'play'
}

export function soundAction(state: PlaybackState): SoundAction {
  if (!state.audioPresent) return 'unavailable'
  return state.muted ? 'unmute' : 'mute'
}

export function showLoading(state: PlaybackState): boolean {
  return !state.ended && !state.firstFrameRendered && (state.playbackRequested || state.isPlaying)
}

export function controlsMayFade(state: PlaybackState): boolean {
  return state.isPlaying && state.firstFrameRendered && !state.screenReaderEnabled && !state.ended
}

export function controlsHidden(state: PlaybackState): boolean {
  return state.controlsFaded && controlsMayFade(state)
}

/** A press along the track as a fraction of it, or null when the track cannot answer. */
export function trackFraction(offset: number, trackStart: number, trackWidth: number): number | null {
  if (!Number.isFinite(offset) || !Number.isFinite(trackStart) || !Number.isFinite(trackWidth)) {
    return null
  }
  if (trackWidth <= 0) return null
  return Math.max(0, Math.min(1, (offset - trackStart) / trackWidth))
}

export function seekFromFraction(state: PlaybackState, fraction: number): number {
  const duration = effectiveDuration(state)
  return clampVideoTime(fraction * duration, duration)
}

export function stepSeek(state: PlaybackState, direction: 1 | -1): number {
  const duration = effectiveDuration(state)
  return clampVideoTime(shownPosition(state) + direction * VIDEO_SEEK_STEP_SECONDS, duration)
}

/** `m:ss`, rounded to the second; anything unreadable is `0:00`. */
export function formatPlaybackTime(seconds: number): string {
  const safe = Number.isFinite(seconds) ? Math.max(0, Math.round(seconds)) : 0
  const minutes = Math.floor(safe / 60)
  return `${minutes}:${String(safe % 60).padStart(2, '0')}`
}
